import { getApiBaseUrl } from '../config/apiConfig';
import type { ApiConfig } from '../types/api';
import { isDevelopment } from '../utils/environment';
import type { TokenProvider } from './apiClient';
import { CalendarService } from './calendarService';
import { LicensingService } from './licensingService';
import { OwnershipService } from './ownershipService';
import { PhoneService } from './phoneService';
import { ProfileService } from './profileService';
import { ReservationService } from './reservationService';

/**
 * Central API manager that coordinates all API services
 *
 * SM-927: The API URL is automatically derived from the current hostname by prepending 'api.'
 * This works for both *.ngmeet.sangoma.com and *.meet.sangoma.com domains.
 */
export class ApiManager {
  public readonly reservation: ReservationService;
  public readonly profile: ProfileService;
  public readonly calendar: CalendarService;
  public readonly ownership: OwnershipService;
  public readonly licensing: LicensingService;
  public readonly phone: PhoneService;

  private currentToken: string | null = null;

  constructor(config?: Partial<ApiConfig>) {
    // Automatically configure base URL based on current hostname
    const isDevMode = isDevelopment();
    const defaultConfig: Partial<ApiConfig> = {
      baseUrl: getApiBaseUrl(isDevMode),
      ...config,
    };

    this.reservation = new ReservationService(defaultConfig);
    this.profile = new ProfileService(defaultConfig);
    this.calendar = new CalendarService(defaultConfig);
    this.ownership = new OwnershipService(defaultConfig);
    this.licensing = new LicensingService(defaultConfig);
    this.phone = new PhoneService(defaultConfig);
  }

  /**
   * Set authentication token for all services
   */
  setAuthToken(token: string): void {
    this.currentToken = token;
    this.reservation.setAuthToken(token);
    this.profile.setAuthToken(token);
    this.calendar.setAuthToken(token);
    this.ownership.setAuthToken(token);
    this.licensing.setAuthToken(token);
    this.phone.setAuthToken(token);
  }

  /**
   * Clear authentication token from all services
   */
  clearAuthToken(): void {
    this.currentToken = null;
    this.reservation.clearAuthToken();
    this.profile.clearAuthToken();
    this.calendar.clearAuthToken();
    this.ownership.clearAuthToken();
    this.licensing.clearAuthToken();
    this.phone.clearAuthToken();
  }

  /**
   * Register a token provider for hosted (MF) mode. Fans out to every
   * sub-client so each per-request bearer comes from the host's scuba
   * bridge rather than a cached static string.
   */
  setTokenProvider(provider: TokenProvider | null): void {
    this.reservation.setTokenProvider(provider);
    this.profile.setTokenProvider(provider);
    this.calendar.setTokenProvider(provider);
    this.ownership.setTokenProvider(provider);
    this.licensing.setTokenProvider(provider);
    this.phone.setTokenProvider(provider);
  }

  /**
   * Get current authentication token
   */
  getCurrentToken(): string | null {
    return this.currentToken;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentToken !== null;
  }

  /**
   * Update configuration for all services
   */
  updateConfig(newConfig: Partial<ApiConfig>): void {
    this.reservation.updateConfig(newConfig);
    this.profile.updateConfig(newConfig);
    this.ownership.updateConfig(newConfig);
    this.licensing.updateConfig(newConfig);
    this.phone.updateConfig(newConfig);
  }

  /**
   * Get the current API base URL
   */
  getApiBaseUrl(): string {
    return getApiBaseUrl(isDevelopment());
  }
}
